import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { Button } from "@/components/ui/button";
import { Check, Phone, Globe, Loader2 } from "lucide-react";
import Marque from "../components/Marque";
import { toast } from "sonner";
import Faq from "@/components/Faq";
import { evenement } from "@/lib/pixel";

// Grille de secours si l'API ne répond pas (mêmes valeurs que PLANS côté serveur).
const PLANS_DEFAUT = [
  { id: "solo", nom_fr: "Solo", nom_en: "Solo", prix_mois: 120, prix_annuel_mois: 88, estimates_per_month: 40, surplus: 2, prix_mois_usd: 84, prix_annuel_mois_usd: 62, surplus_usd: 2 },
  { id: "trio", nom_fr: "Trio", nom_en: "Trio", prix_mois: 199, prix_annuel_mois: 129, estimates_per_month: 100, surplus: 1.5, prix_mois_usd: 139, prix_annuel_mois_usd: 89, surplus_usd: 1.5 },
  { id: "team", nom_fr: "Team", nom_en: "Team", prix_mois: 299, prix_annuel_mois: 199, estimates_per_month: 250, surplus: 1, prix_mois_usd: 209, prix_annuel_mois_usd: 139, surplus_usd: 1 },
  { id: "legend", nom_fr: "Legend", nom_en: "Legend", prix_mois: 399, prix_annuel_mois: 279, estimates_per_month: 500, surplus: 0.5, prix_mois_usd: 279, prix_annuel_mois_usd: 195, surplus_usd: 0.5 },
];

const CONTACT = "quotes@roofborder.com";
// Numéro sans frais : « vanity » à l'affichage, chiffres réels dans le lien tel:.
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";
// Ligne de DÉMONSTRATION : c'est l'agent vocal d'un vrai couvreur. Le visiteur
// appelle en se faisant passer pour un client de toiture et entend exactement
// ce que ses propres clients entendraient.
const TEL_DEMO = "+1 450-328-8648";
const TEL_DEMO_LIEN = "tel:+14503288648";
// Ligne des projets web (« SITE » = 7483), affichée en vanity pour se retenir.
const TEL_SITE = "450-990-SITE (7483)";
const TEL_SITE_LIEN = "tel:+14509907483";

// Textes de la page dans les trois langues (même patron que Accueil.js).
// Les entrées paramétrées sont des fonctions ; tout le reste est une chaîne.
const T = {
  en: {
    connexion: "Sign in",
    surtitre: "Plans",
    h1: "A roof priced in under a minute",
    sousH1: "Enter an address: the system measures the roof by satellite, applies your prices and produces a branded PDF. Answer first, close more often.",
    mensuel: "Monthly",
    annuel: "Billed yearly",
    annuelBadge: "up to 30% off",
    codeLabel: "Referral or offer code (optional)",
    codeValide: (p) => `Valid code — ${p.rabais}% off for ${p.mois} months${p.entreprise ? `, courtesy of ${p.entreprise}` : ""}. On a yearly plan, the discount applies to the whole year.`,
    codeEssai: (p) => `Valid code — ${p.restantes} seat(s) left. You get $${p.montant_usd} off your first month: the Solo plan comes to zero, and a bigger plan costs only the difference. Cancel any time.`,
    codeEpuise: (p) => `All free seats are taken — but your code still gives you ${p.rabais}% off, for as long as you stay subscribed.`,
    codeInconnu: "This code isn't recognized.",
    codeAide: "A roofer referred you to Roof Border? Their code gets you 20% off.",
    paiementIndispo: "Online payment unavailable — write to us.",
    paiementAnnule: "Payment cancelled — nothing was charged.",
    populaire: "Popular",
    parMois: "mo",
    factureAnnuel: (mois) => `billed yearly (instead of ${mois}/mo)`,
    ouAnnuel: (mois) => `or ${mois}/mo billed yearly`,
    // Listes ETAGEES (regle de Jeff 2026-08-03) : Solo a un point de moins que
    // Trio, qui en a un de moins que Team et Legend (7/8/9/9). Les items
    // { t, fort } ressortent en gras : ce sont les differenciateurs.
    inclus: (p, surplus) => {
      const base = [
        { t: `${p.estimates_per_month} satellite estimates / month`, fort: true },
        "Automatic satellite measurement — or enter your own, free of quota",
        "Branded PDF quotes, your prices and colors",
        "Estimate widget for your website — a lead machine",
        "Lead manager with notes and CSV export",
      ];
      // Le tarif de dépassement baisse à chaque palier : en gras, il se compare
      // d'une carte à l'autre.
      const extra = { t: `${surplus} / additional estimate`, fort: true };
      if (p.id === "solo") return [...base, "Manual lead follow-up, one click per lead", extra];
      const plus = [...base,
        { t: "Automatic lead follow-up, on or off per lead", fort: true },
        { t: "5-step follow-up sequence, on your schedule", fort: true },
      ];
      if (p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Salespeople assigned and printed on your quotes", fort: true }, extra];
    },
    commencer: "Get started",
    recepDesc: "An AI voice receptionist that answers your calls 24/7, qualifies clients and takes quote requests — even when you're on a roof.",
    recepAjout: "Added to a Roof Border plan",
    recepFrais: (m) => `${m} one-time programming fee`,
    recepSeul: "Standalone",
    recepMin: (m) => `200 minutes, then ${m}/min`,
    rappel: "Request a call back",
    recepAppelDemo: "Be the roofing client — call",
    recepAppelQuestions: "Ask your questions",
    packNom: "total package",
    packDesc: "The complete system, turnkey: your web presence, your quotes and your calls — all automated.",
    packItems: (estim, min) => [
      "Full custom roofer website",
      `800 satellite quote requests / mo (then ${estim}/quote)`,
      `500 min of Roofer AI Receptionist / mo (then ${min}/min)`,
    ],
    packAnnee: "full year, paid upfront",
    packOu: "or",
    packBtn: "Talk about the package",
    packAppel: "Call and tell us your needs",
    packCourriel: "Or email us your availability",
    packCourrielCorps: "Hello,\n\nI'm interested in the To the Roof package.\n\nCompany:\nName:\nPhone:\nBest times to reach me:\n\nWhat I'm looking for:\n",
    mentions: `Prices in Canadian dollars. Secure payment through Stripe, cancel anytime. Your account opens as soon as the payment clears. The AI receptionist and websites are set up with us: write to ${CONTACT}.`,
    mentionsUsd: `Prices in US dollars. Secure payment through Stripe, cancel anytime. Your account opens as soon as the payment clears. The AI receptionist and websites are set up with us: write to ${CONTACT}.`,
    deviseCad: "$ CAN",
    deviseUsd: "US$",
  },
  fr: {
    connexion: "Connexion",
    surtitre: "Forfaits",
    h1: "Un prix de toiture en moins d'une minute",
    sousH1: "Entrez une adresse : le système mesure le toit par satellite, applique vos prix et produit un PDF à votre image. Répondez le premier, signez plus souvent.",
    mensuel: "Mensuel",
    annuel: "Payé à l'année",
    annuelBadge: "jusqu'à -30 %",
    codeLabel: "Code de parrainage ou d'offre (facultatif)",
    codeValide: (p) => `Code valide — ${p.rabais} % de rabais pendant ${p.mois} mois${p.entreprise ? `, gracieuseté de ${p.entreprise}` : ""}. Sur un forfait annuel, le rabais porte sur l'année complète.`,
    codeEssai: (p) => `Code valide — il reste ${p.restantes} place(s). Vous obtenez ${p.montant_cad} $ de rabais sur votre premier mois : le forfait Solo tombe à zéro, et un forfait plus gros ne vous coûte que la différence. Annulable en tout temps.`,
    codeEpuise: (p) => `Toutes les places gratuites sont prises — mais votre code vous donne quand même ${p.rabais} % de rabais, tant que vous restez abonné.`,
    codeInconnu: "Ce code n'est pas reconnu.",
    codeAide: "Un couvreur vous a recommandé Roof Border ? Son code vous donne 20 % de rabais.",
    paiementIndispo: "Paiement en ligne indisponible — écrivez-nous.",
    paiementAnnule: "Paiement annulé — rien n'a été facturé.",
    populaire: "Populaire",
    parMois: "mois",
    factureAnnuel: (mois) => `facturé annuellement (au lieu de ${mois}/mois)`,
    ouAnnuel: (mois) => `ou ${mois}/mois payé à l'année`,
    inclus: (p, surplus) => {
      const base = [
        { t: `${p.estimates_per_month} estimations satellite / mois`, fort: true },
        "Mesure satellite automatique — ou saisissez les vôtres, hors quota",
        "Soumissions PDF à votre image, vos prix, vos couleurs",
        "Widget d'estimation pour votre site web — une machine à prospects",
        "Gestion des prospects avec notes et export Excel",
      ];
      const extra = { t: `${surplus} / estimation additionnelle`, fort: true };
      if (p.id === "solo") return [...base, "Relance manuelle, un clic par prospect", extra];
      const plus = [...base,
        { t: "Relance automatique, activable prospect par prospect", fort: true },
        { t: "Séquence de 5 suivis programmés, à vos délais", fort: true },
      ];
      if (p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Vendeurs assignés, imprimés sur vos soumissions", fort: true }, extra];
    },
    commencer: "Commencer",
    recepDesc: "Une réceptionniste vocale IA qui répond à vos appels 24/7, qualifie les clients et prend les demandes de soumission — même quand vous êtes sur un toit.",
    recepAjout: "En ajout à un forfait Roof Border",
    recepFrais: (m) => `frais de programmation uniques de ${m}`,
    recepSeul: "Seule, sans forfait",
    recepMin: (m) => `200 minutes, puis ${m}/min`,
    rappel: "Me faire rappeler",
    recepAppelDemo: "Soyez le client — appelez",
    recepAppelQuestions: "Posez vos questions",
    packNom: "package complet",
    packDesc: "Le système au complet, clé en main : votre présence web, vos soumissions et vos appels — tout automatisé.",
    packItems: (estim, min) => [
      "Site web de couvreur entièrement sur mesure",
      `800 estimations satellite / mois (puis ${estim}/estimation)`,
      `500 minutes de Roofer AI Receptionist / mois (puis ${min}/min)`,
    ],
    packAnnee: "l'année complète, payée d'avance",
    packOu: "ou",
    packBtn: "Discuter du package",
    packAppel: "Appelez-nous, dites-nous vos besoins",
    packCourriel: "Ou écrivez-nous vos disponibilités",
    packCourrielCorps: "Bonjour,\n\nJe m'intéresse au package To the Roof.\n\nEntreprise :\nNom :\nTéléphone :\nMeilleurs moments pour me joindre :\n\nCe que je cherche :\n",
    mentions: `Prix en dollars canadiens. Paiement sécurisé par Stripe, annulable en tout temps. Votre compte s'ouvre dès le paiement confirmé. La réceptionniste IA et les sites web se règlent avec nous : écrivez à ${CONTACT}.`,
    mentionsUsd: `Prix en dollars américains. Paiement sécurisé par Stripe, annulable en tout temps. Votre compte s'ouvre dès le paiement confirmé. La réceptionniste IA et les sites web se règlent avec nous : écrivez à ${CONTACT}.`,
    deviseCad: "$ CAN",
    deviseUsd: "$ US",
  },
  es: {
    connexion: "Iniciar sesión",
    surtitre: "Planes",
    h1: "El precio de un techo en menos de un minuto",
    sousH1: "Ingrese una dirección: el sistema mide el techo por satélite, aplica sus precios y produce un PDF con su imagen. Responda primero, cierre más contratos.",
    mensuel: "Mensual",
    annuel: "Pago anual",
    annuelBadge: "hasta 30% de descuento",
    codeLabel: "Código de referencia o de oferta (opcional)",
    codeValide: (p) => `Código válido — ${p.rabais}% de descuento durante ${p.mois} meses${p.entreprise ? `, cortesía de ${p.entreprise}` : ""}. En un plan anual, el descuento aplica al año completo.`,
    codeEssai: (p) => `Código válido — quedan ${p.restantes} lugar(es). Obtiene $${p.montant_usd} de descuento en su primer mes: el plan Solo queda en cero, y un plan mayor solo le cuesta la diferencia. Cancele cuando quiera.`,
    codeEpuise: (p) => `Todos los lugares gratuitos están tomados — pero su código igual le da ${p.rabais}% de descuento, mientras siga suscrito.`,
    codeInconnu: "Este código no se reconoce.",
    codeAide: "¿Un techador le recomendó Roof Border? Su código le da un 20% de descuento.",
    paiementIndispo: "Pago en línea no disponible — escríbanos.",
    paiementAnnule: "Pago cancelado — no se cobró nada.",
    populaire: "Popular",
    parMois: "mes",
    factureAnnuel: (mois) => `facturado anualmente (en lugar de ${mois}/mes)`,
    ouAnnuel: (mois) => `o ${mois}/mes con pago anual`,
    inclus: (p, surplus) => {
      const base = [
        { t: `${p.estimates_per_month} estimados satelitales / mes`, fort: true },
        "Medición satelital automática — o ingrese las suyas, sin consumir cuota",
        "Cotizaciones PDF con su imagen, sus precios y sus colores",
        "Widget de estimados para su sitio web — una máquina de prospectos",
        "Gestión de prospectos con notas y exportación Excel",
      ];
      const extra = { t: `${surplus} / estimado adicional`, fort: true };
      if (p.id === "solo") return [...base, "Seguimiento manual, un clic por prospecto", extra];
      const plus = [...base,
        { t: "Seguimiento automático, activable prospecto por prospecto", fort: true },
        { t: "Secuencia de 5 seguimientos programados, a sus tiempos", fort: true },
      ];
      if (p.id === "trio") return [...plus, extra];
      return [...plus, { t: "Vendedores asignados, impresos en sus cotizaciones", fort: true }, extra];
    },
    commencer: "Empezar",
    recepDesc: "Una recepcionista de voz con IA que contesta sus llamadas 24/7, califica a los clientes y toma las solicitudes de cotización — incluso cuando usted está en un techo.",
    recepAjout: "Agregada a un plan Roof Border",
    recepFrais: (m) => `cargo único de programación de ${m}`,
    recepSeul: "Sola, sin plan",
    recepMin: (m) => `200 minutos, luego ${m}/min`,
    rappel: "Solicitar una llamada",
    recepAppelDemo: "Sea el cliente — llame",
    recepAppelQuestions: "Haga sus preguntas",
    packNom: "paquete completo",
    packDesc: "El sistema completo, llave en mano: su presencia web, sus cotizaciones y sus llamadas — todo automatizado.",
    packItems: (estim, min) => [
      "Sitio web de techador totalmente a la medida",
      `800 estimados satelitales / mes (luego ${estim}/estimado)`,
      `500 min de Roofer AI Receptionist / mes (luego ${min}/min)`,
    ],
    packAnnee: "el año completo, pagado por adelantado",
    packOu: "o",
    packBtn: "Hablar del paquete",
    packAppel: "Llámenos y díganos sus necesidades",
    packCourriel: "O escríbanos su disponibilidad",
    packCourrielCorps: "Hola,\n\nMe interesa el paquete To the Roof.\n\nEmpresa:\nNombre:\nTeléfono:\nMejores horarios para contactarme:\n\nLo que busco:\n",
    mentions: `Precios en dólares canadienses. Pago seguro con Stripe, cancelable en cualquier momento. Su cuenta se abre en cuanto se confirma el pago. La recepcionista IA y los sitios web se contratan con nosotros: escriba a ${CONTACT}.`,
    mentionsUsd: `Precios en dólares estadounidenses. Pago seguro con Stripe, cancelable en cualquier momento. Su cuenta se abre en cuanto se confirma el pago. La recepcionista IA y los sitios web se contratan con nosotros: escriba a ${CONTACT}.`,
    deviseCad: "$ CAN",
    deviseUsd: "US$",
  },
};

export default function Tarifs() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const fr = lang === "fr";
  const L = T[lang] || T.en;
  const [plans, setPlans] = useState(PLANS_DEFAUT);
  const [annuel, setAnnuel] = useState(false);
  // Devise d'affichage et de facturation. Ordre de priorité :
  //   1. le clic du visiteur sur l'interrupteur $ CAN / US$ — il prime toujours ;
  //   2. son PAYS, deviné par son IP côté serveur (/public/devise) ;
  //   3. sa langue (espagnol = marché américain), le temps que la réponse arrive ;
  //   4. CAD, le marché d'origine.
  // Un couvreur du Texas ne doit pas découvrir nos prix en dollars canadiens :
  // il compare avec ce qu'il connaît, et la mauvaise monnaie se lit comme une
  // erreur de notre part.
  const [deviseChoisie, setDeviseChoisie] = useState(null);
  const [devisePays, setDevisePays] = useState(null);
  const devise = deviseChoisie || devisePays || (lang === "es" ? "usd" : "cad");
  const usd = devise === "usd";
  // Montant d'un plan dans la devise affichée (repli CAD si la grille US manque).
  const montant = (p, cle) => (usd ? (p[`${cle}_usd`] ?? p[cle]) : p[cle]);
  const [enCours, setEnCours] = useState("");
  const [code, setCode] = useState("");
  const [parrain, setParrain] = useState(null);   // null = pas vérifié

  // Pays du visiteur, une seule fois au chargement. En cas de panne ou de
  // lenteur, on garde simplement le défaut : la page ne dépend pas de ce appel.
  useEffect(() => {
    let vivant = true;
    api.get("/public/devise")
      .then(({ data }) => { if (vivant && data?.devise) setDevisePays(data.devise); })
      .catch(() => {});
    return () => { vivant = false; };
  }, []);

  // Vérification du code de parrainage, à la frappe (le lien de partage le
  // pré-remplit, mais on accepte aussi la saisie à la main).
  useEffect(() => {
    const propre = code.trim().toUpperCase();
    if (propre.length < 6) { setParrain(null); return; }
    let vivant = true;
    const minuteur = setTimeout(() => {
      api.get("/public/code", { params: { code: propre } })
        .then(({ data }) => { if (vivant) setParrain(data?.valide ? data : false); })
        .catch(() => { if (vivant) setParrain(false); });
    }, 400);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [code]);

  // Achat en ligne. Un visiteur passe par le parcours public (Stripe collecte
  // son courriel, le compte s'ouvre au paiement) ; un couvreur déjà connecté
  // passe par la route authentifiée, qui rattache l'abonnement à son compte.
  const acheter = async (planId) => {
    setEnCours(planId);
    const interval = annuel ? "year" : "month";
    // Départ vers Stripe : c'est l'intention d'achat. Le paiement confirmé, lui,
    // se compte sur /bienvenue — sinon un abandon au checkout serait une vente.
    evenement("InitiateCheckout", {
      content_name: planId, content_category: interval,
      value: montant(PLANS_DEFAUT.find((p) => p.id === planId) || {}, annuel ? "prix_annuel_mois" : "prix_mois"),
      currency: devise === "usd" ? "USD" : "CAD",
    });
    try {
      const { data } = user
        ? await api.post("/stripe/checkout", { plan: planId, interval, devise })
        : await api.post("/public/checkout", { plan: planId, interval, lang, devise, code: code.trim().toUpperCase() });
      if (!data?.url) throw new Error("no url");
      window.location.href = data.url;
    } catch {
      // Paiement en ligne indisponible : on ne laisse pas le client dans le vide.
      toast.error(L.paiementIndispo);
      window.location.href = `mailto:${CONTACT}?subject=${encodeURIComponent(`Forfait ${planId} — Roof Border`)}`;
      setEnCours("");
    }
  };

  useEffect(() => {
    // Retour d'un paiement abandonné : le dire, sinon le client croit avoir payé.
    const params = new URLSearchParams(window.location.search);
    if (params.get("paiement") === "annule") {
      toast(L.paiementAnnule);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Lien de parrainage : le code arrive dans l'URL, on le pose dans le champ.
    const recu = (params.get("code") || "").trim().toUpperCase();
    if (recu) setCode(recu);
    api.get("/plans")
      .then(({ data }) => { if (data?.plans?.length) setPlans(data.plans); })
      .catch(() => {});
    // Une seule fois au montage : le message d'annulation ne doit pas revenir
    // si le visiteur change de langue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format monétaire : « 120 $ » en français, « $120 » en anglais et en espagnol.
  // En dollars US, le suffixe/préfixe l'annonce sans ambiguïté.
  const dollars = (v) => (fr ? (usd ? `${v} $ US` : `${v} $`) : (usd ? `US$${v}` : `$${v}`));
  const surplus = (v) => (fr ? `${String(v).replace(".", ",")} $${usd ? " US" : ""}` : `${usd ? "US$" : "$"}${v}`);

  const inclus = (p) => L.inclus(p, surplus(montant(p, "surplus")));

  // Services hors forfaits (réceptionniste IA, package To the Roof) : grille
  // US = −20 % (règle de Jeff : arrondi au dollar, dizaine ronde −1), les
  // montants sous le dollar prennent le −20 % tel quel.
  // ⚠️ Le tarif à la minute NE suit PAS la règle des −20 % : les deux montants
  // ont été fixés directement par Jeff le 2026-08-04 (0,17 $ CA / 0,13 $ US).
  // `packMin` porte volontairement la MÊME valeur que `minute` : la minute
  // excédentaire coûte le même prix qu'on soit à la réceptionniste seule ou
  // dans le package. Les garder synchronisés.
  const SVC = usd
    ? { setupAjout: 79, mensuelAjout: 135, setupSeul: 119, mensuelSeul: 199,
        minute: "0.13", packAn: 6559, packInit: 2399, packMois: 399,
        packEstim: "0.40", packMin: "0.13" }
    : { setupAjout: 99, mensuelAjout: 169, setupSeul: 149, mensuelSeul: 249,
        minute: "0.17", packAn: 8200, packInit: 3000, packMois: 500,
        packEstim: "0.50", packMin: "0.17" };

  // Réceptionniste IA payée à l'année : rabais FIXE par mois, le même montant
  // dans les deux devises (décision de Jeff, 2026-08-04) — 10 $ sur la formule
  // en ajout, 25 $ sur la formule seule. Le rabais suit l'interrupteur
  // Mensuel / Annuel du haut de page, comme les forfaits.
  const RABAIS_AN_AJOUT = 10;
  const RABAIS_AN_SEUL = 25;
  const recepAjoutMois = annuel ? SVC.mensuelAjout - RABAIS_AN_AJOUT : SVC.mensuelAjout;
  const recepSeulMois = annuel ? SVC.mensuelSeul - RABAIS_AN_SEUL : SVC.mensuelSeul;

  return (
    <div className="min-h-screen bg-app text-ink">
      {/* entête */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link to="/login">
              <Button variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink h-9">
                {L.connexion}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-3">{L.surtitre}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mb-4">
            {L.h1}
          </h1>
          <p className="text-ink-soft max-w-2xl mx-auto">
            {L.sousH1}
          </p>
        </div>

        {/* bascule de devise : $ CAN ou US$, les montants suivent */}
        <div className="flex items-center justify-center mb-4">
          <div className="inline-flex rounded-md border border-line-strong overflow-hidden" data-testid="bascule-devise">
            {[["cad", L.deviseCad], ["usd", L.deviseUsd]].map(([d, nom]) => (
              <button
                key={d}
                data-testid={`devise-${d}`}
                onClick={() => setDeviseChoisie(d)}
                className={`px-4 py-1.5 text-xs font-bold transition-colors ${
                  devise === d ? "bg-orange-600 text-white" : "bg-field text-ink-soft hover:bg-hover"
                }`}
              >
                {nom}
              </button>
            ))}
          </div>
        </div>

        {/* bascule mensuel / annuel */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <button
            onClick={() => setAnnuel(false)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${!annuel ? "bg-orange-600 text-white" : "bg-hover text-ink-soft hover:bg-hover-strong hover:text-ink"}`}
          >
            {L.mensuel}
          </button>
          {/* VERT au survol ET une fois sélectionné : c'est le bouton qui fait
              économiser, et le vert est déjà la couleur de l'économie sur cette
              page (badge de rabais, forfait Trio, package To the Roof).
              L'orange reste à « Mensuel », l'option ordinaire.
              `group` permet au badge de suivre le survol du bouton. */}
          <button
            onClick={() => setAnnuel(true)}
            className={`group px-4 py-2 rounded-md text-sm font-semibold transition-colors ${annuel ? "bg-emerald-600 text-white" : "bg-hover text-ink-soft hover:bg-emerald-600 hover:text-white"}`}
          >
            {L.annuel}
            {/* Le badge doit rester lisible SUR SON PROPRE FOND : blanc dès que
                le fond est plein (sélectionné ou survolé), emerald-500 au repos.
                Il était en orange sur orange, donc invisible. */}
            <span className={`ml-2 text-xs ${annuel ? "text-white/90" : "text-emerald-500 group-hover:text-white"}`}>
              {L.annuelBadge}
            </span>
          </button>
        </div>

        {/* code de parrainage */}
        <div className="max-w-md mx-auto mb-10">
          <label className="block text-xs uppercase tracking-widest text-ink-faint mb-2 text-center">
            {L.codeLabel}
          </label>
          <input
            data-testid="code-parrainage"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="RB-XXXXXX"
            maxLength={12}
            className={`w-full bg-field border rounded-md px-4 py-2.5 text-center font-mono tracking-widest outline-none transition-colors ${
              parrain === false ? "border-red-500/60" : parrain ? "border-emerald-500/60" : "border-line-strong focus:border-orange-500"
            }`}
          />
          {/* Le même champ accepte deux sortes de codes : un parrainage (rabais)
              et une offre d'essai (premier mois offert). Le serveur tranche et
              renvoie le type ; le message doit suivre, sinon un couvreur qui a
              reçu 30 jours gratuits lirait « 20 % de rabais ». */}
          {parrain && (
            <p data-testid="code-valide" className="text-sm text-emerald-500 mt-2 text-center">
              {parrain.type === "campagne" ? L.codeEssai(parrain)
                : parrain.type === "campagne_epuisee" ? L.codeEpuise(parrain)
                : L.codeValide(parrain)}
            </p>
          )}
          {parrain === false && (
            <p className="text-sm text-red-400 mt-2 text-center">
              {L.codeInconnu}
            </p>
          )}
          {parrain === null && !code && (
            <p className="text-xs text-ink-faint mt-2 text-center">
              {L.codeAide}
            </p>
          )}
        </div>

        {/* forfaits */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {plans.map((p) => {
            // Forfait mis en avant. Trio depuis le 2026-08-04 : c'est le
            // premier palier avec la relance automatique, donc celui qui fait
            // vraiment basculer un couvreur — et le prix d'entrée y reste doux.
            const vedette = p.id === "trio";
            const prix = montant(p, annuel ? "prix_annuel_mois" : "prix_mois");
            return (
              // Le survol soulève la carte : sur une grille de quatre, c'est ce
              // qui dit au visiteur laquelle il est en train de comparer.
              <div
                key={p.id}
                className={`group relative rounded-xl border p-6 flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-releve ${
                  vedette
                    ? "border-emerald-500/60 bg-emerald-500/[0.06] hover:border-emerald-500"
                    : "border-line bg-surface hover:border-line-strong"
                }`}
              >
                {vedette && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-md shadow-carte">
                    {L.populaire}
                  </span>
                )}
                <h2 className="font-heading font-extrabold text-xl tracking-tight">{fr ? p.nom_fr : p.nom_en}</h2>
                <div className="mt-3 mb-1">
                  <span className="font-heading text-4xl font-extrabold tracking-tighter">{dollars(prix)}</span>
                  <span className="text-ink-faint text-sm"> / {L.parMois}</span>
                </div>
                <p className="text-xs text-ink-faint mb-5 min-h-[16px]">
                  {annuel
                    ? L.factureAnnuel(dollars(montant(p, "prix_mois")))
                    : L.ouAnnuel(dollars(montant(p, "prix_annuel_mois")))}
                </p>
                <ul className="space-y-2.5 text-sm text-ink-soft flex-1">
                  {inclus(p).map((li, i) => {
                    // { t, fort } = différenciateur du forfait : en gras, à
                    // l'encre pleine, pour qu'il ressorte au premier survol.
                    const fort = typeof li === "object" && li.fort;
                    const texte = typeof li === "object" ? li.t : li;
                    return (
                      <li key={i} className={`flex gap-2 ${fort ? "font-semibold text-ink" : ""}`}>
                        <Check className="h-4 w-4 text-brand-ink shrink-0 mt-0.5" />
                        {texte}
                      </li>
                    );
                  })}
                </ul>
                <Button
                  onClick={() => acheter(p.id)}
                  disabled={!!enCours}
                  data-testid={`acheter-${p.id}`}
                  className={`mt-6 w-full rounded-md font-semibold transition-colors ${
                    vedette
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-hover-strong hover:bg-brand hover:text-white"
                  }`}
                >
                  {enCours === p.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {L.commencer}
                </Button>
              </div>
            );
          })}
        </div>

        {/* réceptionniste IA */}
        <div className="grid lg:grid-cols-2 gap-5 mb-16">
          <div className="rounded-xl border border-line bg-surface p-7 shadow-carte transition-all duration-200 hover:-translate-y-1 hover:shadow-releve hover:border-line-strong">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-5 w-5 text-brand-ink" />
              <h2 className="font-heading font-extrabold text-xl tracking-tight">Roofer AI Receptionist</h2>
            </div>
            <p className="text-sm text-ink-soft mb-5">
              {L.recepDesc}
            </p>
            <div className="space-y-4 text-sm">
              <div className="flex items-start justify-between gap-4 border-b border-line pb-4">
                <div>
                  <div className="font-semibold">{L.recepAjout}</div>
                  <div className="text-ink-faint text-xs mt-1">{L.recepFrais(dollars(SVC.setupAjout))}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-extrabold text-2xl">{dollars(recepAjoutMois)}<span className="text-sm text-ink-faint font-normal"> / {L.parMois}</span></div>
                  {annuel && (
                    <div className="text-emerald-500 text-xs font-semibold">{L.factureAnnuel(dollars(SVC.mensuelAjout))}</div>
                  )}
                  <div className="text-ink-faint text-xs">{L.recepMin(surplus(SVC.minute))}</div>
                </div>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold">{L.recepSeul}</div>
                  <div className="text-ink-faint text-xs mt-1">{L.recepFrais(dollars(SVC.setupSeul))}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-extrabold text-2xl">{dollars(recepSeulMois)}<span className="text-sm text-ink-faint font-normal"> / {L.parMois}</span></div>
                  {annuel && (
                    <div className="text-emerald-500 text-xs font-semibold">{L.factureAnnuel(dollars(SVC.mensuelSeul))}</div>
                  )}
                  <div className="text-ink-faint text-xs">{L.recepMin(surplus(SVC.minute))}</div>
                </div>
              </div>
            </div>
            {/* Deux lignes vivantes plutôt qu'un mailto : la meilleure démo
                d'une réceptionniste vocale, c'est de l'appeler. La première fait
                vivre l'appel du CLIENT d'un couvreur, la seconde répond aux
                questions sur le service. `tel:` compose directement au mobile. */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href={TEL_DEMO_LIEN} data-testid="recep-appel-demo">
                <Button variant="outline" className="w-full h-auto py-2.5 flex flex-col gap-0.5 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
                  <span className="text-xs font-semibold">{L.recepAppelDemo}</span>
                  <span className="font-heading font-bold text-brand-ink">{TEL_DEMO}</span>
                </Button>
              </a>
              <a href={TEL_LIEN} data-testid="recep-appel-questions">
                <Button variant="outline" className="w-full h-auto py-2.5 flex flex-col gap-0.5 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
                  <span className="text-xs font-semibold">{L.recepAppelQuestions}</span>
                  <span className="font-heading font-bold text-brand-ink">{TEL_AFFICHE}</span>
                </Button>
              </a>
            </div>
          </div>

          {/* package complet — même vert que le forfait Trio mis en avant :
              les deux offres qu'on pousse parlent d'une seule voix, l'orange
              reste la couleur de la marque et des actions ordinaires. */}
          <div className="rounded-xl border border-emerald-500/60 bg-gradient-to-b from-emerald-500/[0.08] to-transparent p-7 shadow-carte transition-all duration-200 hover:-translate-y-1 hover:shadow-releve hover:border-emerald-500">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-5 w-5 text-emerald-500" />
              <h2 className="font-heading font-extrabold text-xl tracking-tight">To the Roof — {L.packNom}</h2>
            </div>
            <p className="text-sm text-ink-soft mb-5">
              {L.packDesc}
            </p>
            <ul className="space-y-2.5 text-sm text-ink-soft">
              {L.packItems(surplus(SVC.packEstim), surplus(SVC.packMin)).map((li, i) => (
                <li key={i} className="flex gap-2">
                  <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  {li}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-end gap-6 flex-wrap">
              <div>
                <div className="font-heading font-extrabold text-3xl tracking-tighter">{dollars(SVC.packAn)}</div>
                <div className="text-xs text-ink-faint">{L.packAnnee}</div>
              </div>
              <div className="text-ink-faint text-sm pb-1">{L.packOu}</div>
              <div>
                <div className="font-heading font-extrabold text-3xl tracking-tighter">{dollars(SVC.packInit)} <span className="text-lg">+ {dollars(SVC.packMois)}<span className="text-sm text-ink-faint font-normal">/{L.parMois}</span></span></div>
              </div>
            </div>
            {/* Deux chemins pour entrer en contact : la voix d'abord (c'est un
                projet sur mesure, il se cadre en parlant), le courriel pour qui
                appelle mal à l'aise ou hors des heures. Le mailto pré-remplit
                le sujet ET le corps : le couvreur n'a plus qu'à compléter. */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href={TEL_SITE_LIEN} data-testid="pack-appel">
                <Button className="w-full h-auto py-2.5 flex flex-col gap-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">
                  <span className="text-xs font-semibold">{L.packAppel}</span>
                  <span className="font-heading font-bold">{TEL_SITE}</span>
                </Button>
              </a>
              <a
                href={`mailto:${CONTACT}?subject=${encodeURIComponent("To the Roof — package complet")}&body=${encodeURIComponent(L.packCourrielCorps)}`}
                data-testid="pack-courriel"
              >
                <Button variant="outline" className="w-full h-auto py-2.5 flex flex-col gap-0.5 border-emerald-500/40 rounded-md hover:bg-hover-strong hover:text-ink">
                  <span className="text-xs font-semibold">{L.packCourriel}</span>
                  <span className="font-heading font-bold text-emerald-500">{CONTACT}</span>
                </Button>
              </a>
            </div>
          </div>
        </div>

        <Faq className="mb-16" />

        <p className="text-center text-xs text-ink-faint">
          {usd ? L.mentionsUsd : L.mentions}
        </p>
        <div className="flex items-center justify-center gap-3 mt-4 text-xs text-ink-faint">
          <Link to="/confidentialite" className="hover:text-ink transition-colors">
            {fr ? "Confidentialité" : lang === "es" ? "Privacidad" : "Privacy"}
          </Link>
          <span>·</span>
          <Link to="/conditions" className="hover:text-ink transition-colors">
            {fr ? "Conditions" : lang === "es" ? "Términos" : "Terms"}
          </Link>
        </div>
      </main>
    </div>
  );
}
